import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useConvexAuth } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../../../backend/convex/_generated/api';
import type { Id } from '../../../../backend/convex/_generated/dataModel';
import { RunnerLayout } from '../components/RunnerLayout';
import { Controls } from '../components/Controls';
import { ConfirmModal } from '../components/ConfirmModal';
import { useCodeRunner } from '../hooks/useCodeRunner';
import { useAssessmentSession } from '../hooks/useAssessmentSession';
import { DEFAULT_LANGUAGE } from '../config/languages';
import type { TestResult } from '../types/worker';
import type { LanguageId } from '../types/language';
import { NotFound } from './NotFound';
import '../App.css';

export const AssessmentRunner = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { isLoading: isAuthLoading } = useConvexAuth();
  const { user } = useUser();

  const assignment = useQuery(api.assignments.get, 
    (assignmentId && !isAuthLoading) ? { id: assignmentId as Id<"assessment_assignments"> } : "skip"
  );
  const assessment = useQuery(api.assessments.get, assignment ? { id: assignment.assessmentId } : "skip");
  const session = useQuery(api.sessions.getByAssignment, 
    assignmentId ? { assignmentId: assignmentId as Id<"assessment_assignments"> } : "skip"
  );
  
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const problemId = assessment?.problemSets[currentProblemIndex];
  
  const problem = useQuery(api.problems.get, 
    (problemId && !isAuthLoading) ? { id: problemId } : "skip"
  );

  const [language] = useState<LanguageId>(DEFAULT_LANGUAGE);
  const { status, runTests } = useCodeRunner(language);

  const [currentCode, setCurrentCode] = useState<string>('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [completedProblems, setCompletedProblems] = useState<Set<string>>(new Set());

  // Timer state
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!session || !assessment?.timeLimitMinutes) return;

    const endTime = session.startTime + assessment.timeLimitMinutes * 60 * 1000;
    
    const updateTimer = () => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      
      if (remaining === 0) {
        // Auto-finish assessment
        finishSession();
        navigate('/');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [session, assessment?.timeLimitMinutes, navigate]);

  const formatTimer = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Session management
  const { updateProgress, finishSession } = useAssessmentSession(assignmentId as Id<"assessment_assignments">);

  useEffect(() => {
    if (problem) {
      setCurrentCode(problem.starterCode);
    }
  }, [problem]);

  useEffect(() => {
    updateProgress(completedProblems.size);
  }, [completedProblems.size, updateProgress]);

  // Results state
  const [resultsMode, setResultsMode] = useState<'idle' | 'code' | 'tests'>('idle');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [outputStdout, setOutputStdout] = useState('');
  const [outputStderr, setOutputStderr] = useState('');
  const [outputError, setOutputError] = useState('');

  const createSubmission = useMutation(api.submissions.createSubmission);

  if (assignment === undefined || (assignment !== null && (assessment === undefined || session === undefined))) {
    return <div className="app-container flex items-center justify-center">Loading assessment...</div>;
  }

  if (assignment === null || assessment === null || !assessment) {
    return <NotFound />;
  }

  const handleRun = async () => {
    if (!problem) return;
    const result = await runTests(currentCode, (problem as any).functionName, (problem as any).publicTests);
    setResultsMode('tests');
    setTestResults(result.results);
    setOutputStdout(result.stdout);
    setOutputStderr(result.stderr);
    setOutputError(result.error || '');
  };

  const handleSubmit = async () => {
    setShowSubmitModal(true);
  };

  const confirmSubmit = async () => {
    if (!problem || !user || !assignment || !assessment) return;

    try {
      await createSubmission({
        workspaceId: assessment.workspaceId,
        assessmentId: assessment._id,
        problemId: problem.id,
        language: language,
        source: currentCode,
      });
      
      setCompletedProblems(prev => new Set(prev).add(problem.id));
      
      if (currentProblemIndex < assessment.problemSets.length - 1) {
        setCurrentProblemIndex(prev => prev + 1);
      } else {
        await finishSession();
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to record submission:', error);
    } finally {
      setShowSubmitModal(false);
    }
  };

  const headerContent = (
    <div className="flex items-center gap-4 ml-4 px-4 border-l border-border">
      <div className="flex flex-col">
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Assessment</span>
        <span className="text-sm font-bold truncate max-w-[200px]">{assessment.title}</span>
      </div>
      <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-xs font-bold">
        <span>Problem {currentProblemIndex + 1} of {assessment.problemSets.length}</span>
      </div>
      {timeLeft !== null && (
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold border transition-colors ${
          timeLeft < 300000 
            ? 'bg-destructive/10 text-destructive border-destructive/20 animate-pulse' 
            : 'bg-info/10 text-info border-info/20'
        }`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          {formatTimer(timeLeft)}
        </div>
      )}
    </div>
  );

  const controls = (
    <Controls
      status={status}
      isLoggedIn={!!user}
      onRun={handleRun}
      onSubmit={handleSubmit}
      onResetCode={() => setCurrentCode(problem?.starterCode || '')}
    />
  );

  return (
    <>
      <ConfirmModal
        isOpen={showSubmitModal}
        title="Submit Problem"
        message={assessment && currentProblemIndex < assessment.problemSets.length - 1 
          ? "Are you sure you want to submit this problem and move to the next one?"
          : "Are you sure you want to submit your final problem and finish the assessment?"}
        confirmText={assessment && currentProblemIndex < assessment.problemSets.length - 1 ? "Next Problem" : "Finish Assessment"}
        confirmVariant="success"
        onConfirm={confirmSubmit}
        onCancel={() => setShowSubmitModal(false)}
      />

      <RunnerLayout
        problem={problem as any}
        code={currentCode}
        onCodeChange={setCurrentCode}
        language={language}
        onRun={handleRun}
        resultsMode={resultsMode}
        testResults={testResults}
        stdout={outputStdout}
        stderr={outputStderr}
        error={outputError}
        headerContent={headerContent}
        controls={controls}
      />
    </>
  );
};

